/**
 * ============================================================================
 * FamilyUnitChip — the LIVING FAMILY NETWORK companion-card overlay
 * ----------------------------------------------------------------------------
 * One purpose: render a small heart-marked companion chip ATTACHED to a
 * placed bloodline card so that any spouse / partner / in-law / step / co-
 * parent / blended-household relationship is ALWAYS emotionally visible —
 * even when the strict generational pyramid layout cannot place that person
 * on a tier of their own.
 *
 * ARCHITECTURAL CONTRACT (per the May 2026 "Family Unit Layer" spec):
 *
 *   1. The bloodline pyramid (parent_ids) renders ANCESTRY only. It does
 *      NOT need to find a tier for spouses / partners / in-laws.
 *   2. The Family Unit Layer (this component) renders any member whose
 *      ONLY structural connection to the visible tree is a spouse/partner
 *      edge to an already-placed bloodline card.
 *   3. If a Family Unit member is also placeable on a tier (their partner
 *      is the viewer, or they have parent_ids of their own that resolve),
 *      the pyramid layout still wins — they render as a normal couple unit
 *      with the heart-line connector. The chip ONLY appears for members
 *      the pyramid layout could NOT place beside their partner.
 *   4. Tap = open profile. The chip is a real button, never just decoration.
 *   5. NEVER touches DB. NEVER mutates members. Pure overlay.
 *
 * Visual language:
 *   • Brand-derived heart pip on the LEFT — instantly reads as "this is a partner /
 *     family-unit member" rather than a bloodline relative while inheriting
 *     the already-resolved canonical root presentation of its anchor.
 *   • Tiny avatar (24px) so the chip stays compact even when stacked.
 *   • Small derived label below the name ("Son-in-law to Marion",
 *     "Mother-in-law", "Step-Parent", "Co-parent") so the user understands
 *     why this person is here and how they connect.
 *   • Hover / focus presentation inherits the already-resolved canonical
 *     visual root class of the anchor card. This component never derives
 *     family branch or geometry from color. Pure cosmetic only.
 * ============================================================================
 */
import React from 'react';
import { Heart, User as UserIcon, Eye } from 'lucide-react';
import type { GraphMember } from '@/contexts/FamilyGraphContext';

import {
  CANONICAL_TREE_FAMILY_UNIT_DESCRIPTOR_VISUAL_CLASS,
  CANONICAL_TREE_FAMILY_UNIT_HEART_VISUAL_CLASS,
  CANONICAL_TREE_FAMILY_UNIT_VISUAL_CLASS,
  type CanonicalTreeVisualRootClass,
} from '@/governor/tree/CanonicalTreeVisualPresentationAuthority';

export const FAMILY_UNIT_CHIP_WIDTH = 178;
export const FAMILY_UNIT_CHIP_HEIGHT = 34;
export const FAMILY_UNIT_CHIP_FIRST_OFFSET = 8;
export const FAMILY_UNIT_CHIP_STACK_STEP = 38;

interface FamilyUnitChipProps {
  /** The companion member rendered as a chip (e.g. Terrance Lewis Sr.). */
  member: GraphMember;
  /** Anchor's display name — shown in the descriptor ("Son-in-law to Marion"). */
  anchorName: string;
  /**
   * Canonical visual root class inherited from the already-placed anchor.
   *
   * The chip never derives branch, side, topology, or geometry itself.
   */
  visualRootClass: CanonicalTreeVisualRootClass;
  /**
   * Pre-derived viewer-perspective relationship label for the member.
   * Computed by the kinship engine in FamilyTreeView via kinshipLabel —
   * we never recompute it here so the chip stays a pure presentation
   * component with no engine dependency.
   *
   * Examples:
   *   • "Son-in-law"           (viewer's child's spouse)
   *   • "Daughter-in-law"
   *   • "Brother-in-law"       (viewer's spouse's brother, OR sibling's husband)
   *   • "Mother-in-law"
   *   • "Step-Parent"
   *   • "Spouse / Partner"     (anchor's partner, no in-law derivation)
   */
  derivedLabel: string;
  /** Click handler — opens the profile drawer for this member. */
  onOpen: () => void;
  /**
   * Vertical stack index — lets multiple chips on the same anchor card
   * (e.g. an ex-spouse + a current partner + a co-parent) stack
   * cleanly without overlapping.
   */
  stackIndex: number;
}

const FamilyUnitChip: React.FC<FamilyUnitChipProps> = ({
  member,
  anchorName,
  visualRootClass,
  derivedLabel,
  onOpen,
  stackIndex,
}) => {
  // Stack chips beneath the card using the governed companion geometry.
  const offsetY =
    FAMILY_UNIT_CHIP_FIRST_OFFSET +
    stackIndex * FAMILY_UNIT_CHIP_STACK_STEP;

  // Compose the descriptor — when the derived label already references a
  // generational role (Son-in-law, Daughter-in-law, etc.) we suffix
  // " to {anchor}" so the user immediately sees both sides of the unit.
  // For symmetric labels like "Spouse / Partner" we just show the role.
  const descriptor = (() => {
    const lbl = (derivedLabel || '').trim();
    if (!lbl) return `Family unit · ${anchorName}`;
    const suffixable = /in-law|by marriage|step|co-parent|coparent/i.test(lbl);
    if (suffixable) return `${lbl} to ${anchorName}`;
    return lbl;
  })();

  return (
    <button
      type="button"
      onClick={(e) => {
        e.stopPropagation();
        onOpen();
      }}
      title={`Open ${member.name}'s profile`}
      className={`
        ${visualRootClass}
        ${CANONICAL_TREE_FAMILY_UNIT_VISUAL_CLASS}
        absolute left-1/2 -translate-x-1/2
        w-[178px] h-[34px]
        flex items-center gap-2 pl-1.5 pr-2
        bg-white rounded-full shadow-md border-2
        transition hover:shadow-lg hover:-translate-y-[1px] hover:scale-[1.02]
        focus:outline-none
      `}
      style={{ top: `calc(100% + ${offsetY}px)` }}
    >
      <span
        className={`shrink-0 w-6 h-6 rounded-full ${CANONICAL_TREE_FAMILY_UNIT_HEART_VISUAL_CLASS} flex items-center justify-center`}
        aria-hidden
      >
        <Heart className="w-3 h-3" />
      </span>
      <span className="shrink-0 w-6 h-6 rounded-full overflow-hidden bg-slate-100 ring-1 ring-slate-200 flex items-center justify-center">
        {member.photoUrl ? (
          <img
            src={member.photoUrl}
            alt={member.name}
            className="w-full h-full object-cover"
          />
        ) : (
          <UserIcon className="w-3 h-3 text-slate-500" />
        )}
      </span>
      <span className="min-w-0 flex-1 text-left leading-tight">
        <span className="block text-[11px] font-bold text-slate-900 truncate">
          {member.name.split(' ')[0]}{' '}
          <span className="font-normal text-slate-500">
            {member.name.split(' ').slice(1).join(' ')}
          </span>
        </span>
        <span className={`block text-[9px] font-semibold ${CANONICAL_TREE_FAMILY_UNIT_DESCRIPTOR_VISUAL_CLASS} truncate`}>
          {descriptor}
        </span>
      </span>
      <Eye
        className="w-3.5 h-3.5 text-slate-400 shrink-0"
        aria-hidden
      />
    </button>
  );
};

export default FamilyUnitChip;
